import { Loader } from '@10xLabs/react-web/components/Loader'
import { useLazyQuery } from '@apollo/client'
import { ReactElement, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'

import EmptyState from 'components/v2/EmptyState'
import { NewPartnerBreadcrumb } from 'features/Partners/components/NewPartner/Breadcrumb'
import { NewPartnerStep1 } from 'features/Partners/components/NewPartner/Step1'
import { NewPartnerStep2 } from 'features/Partners/components/NewPartner/Step2'
import { NewPartnerStep3 } from 'features/Partners/components/NewPartner/Step3'
import { NewPartnerStep } from 'features/Partners/types'
import { mapPartnerToStep2Values } from 'features/Partners/utils'
import { GET_PARTNER } from 'features/Partners/queries-legacy'
import { GTBusinessPartner } from 'gql/types.generated'

const BreadcrumbWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`

export const NewPartner = ({
  partner,
  isEdit
}: {
  partner?: GTBusinessPartner
  isEdit?: boolean
}): ReactElement | null => {
  let content: ReactElement = <></>
  const navigate = useNavigate()

  const [stepNb, setStepNb] = useState(NewPartnerStep.DATA)
  const [completedSteps, setCompletedSteps] = useState<NewPartnerStep[]>([])
  const [searchParams] = useSearchParams()
  const [getPartner, { data: partnerData, loading: queryLoading }] = useLazyQuery<{
    businessPartner: GTBusinessPartner
  }>(GET_PARTNER, {
    fetchPolicy: 'network-only'
  })
  const partnerId = searchParams.get('partnerId') || partner?.id

  if (queryLoading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else {
    switch (stepNb) {
      case NewPartnerStep.DATA:
        content = (
          <NewPartnerStep1
            partner={partner || partnerData?.businessPartner}
            isEdit={isEdit}
            onSubmit={(): void => {
              setStepNb(NewPartnerStep.GENERAL_INFORMATION)
              setCompletedSteps([...completedSteps, NewPartnerStep.DATA])
            }}
            onCancel={(): void => {
              navigate('/partners')
            }}
          />
        )
        break
      case NewPartnerStep.GENERAL_INFORMATION:
        content = (
          <NewPartnerStep2
            partner={mapPartnerToStep2Values(partner || partnerData?.businessPartner)}
            isEdit={isEdit}
            onSubmit={(): void => {
              setStepNb(NewPartnerStep.DOCS)
              setCompletedSteps([...completedSteps, NewPartnerStep.GENERAL_INFORMATION])
            }}
            onCancel={(): void => {
              getPartner({
                variables: {
                  id: partnerId
                }
              })
              setStepNb(NewPartnerStep.DATA)
            }}
          />
        )
        break
      case NewPartnerStep.DOCS:
        content = (
          <NewPartnerStep3
            partner={partner || partnerData?.businessPartner}
            isEdit={isEdit}
            onSubmit={(): void => {
              navigate('/partners')
            }}
            onCancel={(): void => {
              getPartner({
                variables: {
                  id: partnerId
                }
              })
              setStepNb(NewPartnerStep.GENERAL_INFORMATION)
            }}
          />
        )
        break
    }
  }

  const handleSetStepNb = (step: NewPartnerStep): void => {
    setStepNb(step)

    if (step < stepNb) {
      getPartner({
        variables: {
          id: partnerId
        }
      })
    }
  }

  return (
    <>
      <BreadcrumbWrapper>
        <NewPartnerBreadcrumb
          currentStep={stepNb}
          isEdit={isEdit}
          setStepNb={handleSetStepNb}
          completedSteps={completedSteps}
        />
      </BreadcrumbWrapper>
      {content}
    </>
  )
}
